import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { LoadingState } from '@/components/ui/LoadingState'
import type { VenueCampaignEstimateView } from '@/lib/venue/campaignTypes'
import { targetCountText } from '@/lib/venue/campaignViewModel'
import { formatAmount } from '@/lib/venue/format'

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slateish-600">{label}</p>
      <p className="text-base font-semibold text-ink-700">{value}</p>
    </div>
  )
}

/**
 * 실시간 예상 비용 카드(요구사항 2 — "조건 변경 시 예상 발송 정보(대상 지역, 광고 동의 대상 수, 예상
 * 발송 건수, 건당 단가, 예상 총 비용)를 미리보기 API로 실시간 갱신. 실제 비용은 실제 발송 건수 기준
 * 확정임을 명시").
 *
 * 🚨 `targetCount`/`estimatedSendCount`의 `null`은 "0명"이 아니라 "산출 불가"다(#731 §2). `estimateAvailable
 * === false`면 발송·비용 산출 자체가 막혀 있다는 뜻이라 사유(`unavailableMessage`)를 그대로 보여준다.
 * 🚨 단가(`unitPriceAmount`)는 하드코딩하지 않는다 — `0`은 "공짜"가 아니라 "정책 미설정"이다.
 */
export function PromotionEstimateCard({
  estimate,
  isLoading,
  isError,
  errorMessage,
}: {
  estimate: VenueCampaignEstimateView | undefined
  isLoading: boolean
  isError: boolean
  errorMessage?: string
}) {
  return (
    <Card>
      <CardHeader title="예상 발송 정보" description="지역·시간대를 바꾸면 자동으로 다시 계산됩니다." />
      <CardBody className="space-y-3">
        {isLoading ? <LoadingState rows={2} label="예상 비용을 계산하는 중" /> : null}

        {isError ? <p className="text-xs text-danger-500">{errorMessage ?? '예상 비용을 불러오지 못했습니다.'}</p> : null}

        {!isLoading && !isError && estimate ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatItem
                label="대상 지역"
                value={
                  estimate.region
                    ? [estimate.region.stateName, estimate.region.cityName].filter(Boolean).join(' ')
                    : '-'
                }
              />
              <StatItem label="광고 동의 대상 수" value={targetCountText(estimate.targetCount)} />
              <StatItem label="예상 발송 건수" value={targetCountText(estimate.estimatedSendCount)} />
              <StatItem
                label="건당 단가"
                value={estimate.unitPriceAmount > 0 ? formatAmount(estimate.unitPriceAmount) : '정책 미설정'}
              />
              <StatItem label="예상 총 비용" value={formatAmount(estimate.estimatedTotalCost)} />
            </div>

            {!estimate.estimateAvailable ? (
              <p className="text-xs text-danger-500">{estimate.unavailableMessage ?? '예상 비용을 산출할 수 없습니다.'}</p>
            ) : (
              <p className="text-xs text-slateish-600">실제 비용은 실제 발송 건수를 기준으로 확정됩니다.</p>
            )}
          </>
        ) : null}

        {!isLoading && !isError && !estimate ? (
          <p className="text-xs text-slateish-600">발송 지역을 선택하면 예상 비용이 표시됩니다.</p>
        ) : null}
      </CardBody>
    </Card>
  )
}
